<script lang="ts">
import { defineComponent, onMounted, ref, Ref, watch } from 'vue'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import { noop } from 'lodash'
import { extend } from 'vee-validate'
import { dialogService } from '@siteminder/sui-core/services'
import { PASSWORD_EXPRESSION } from '@/constants'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import * as authService from '@/services/auth'
import { useI18n } from '../../setup/setup-i18n'
import { Tenant, User } from '../../types'
import { router, useRoute } from '../../router'

const { useState: useUserStoreState, useActions: useUserStoreActions } = createNamespacedHelpers('User')
const { useState: useLoginDomainStoreState } = createNamespacedHelpers('LoginDomain')

export default defineComponent({
  name: 'ResetPasswordUpdatePage',

  components: {
    DefaultLayout,
  },

  setup() {

    const { t } = useI18n()
    const currentRoute = useRoute()

    const user = useUserStoreState(['user']).user as Ref<User>
    const { applyResetPassword } = useUserStoreActions(['applyResetPassword'])
    const tenant = useLoginDomainStoreState(['tenant']).tenant as Ref<Tenant>

    const loading = ref(false)
    const errorMessage = ref(null)

    // these are template refs which can be referenced in the template
    const form = ref(null)
    const password = ref(null)
    const confirmPassword = ref(null)

    const draft = ref({
      password: '',
      confirmPassword: '',
    })

    const passwordExpressionRegex = new RegExp(PASSWORD_EXPRESSION)

    // Force the entire form to revalidate on change, which keeps the
    // password fields "match" validator in sync
    watch(draft, async (newDraft) => {

      if (newDraft.password && password.value.validate) {
        password.value.validate()
      }

      if (newDraft.confirmPassword && confirmPassword.value.validate) {
        confirmPassword.value.validate()
      }

    }, { deep: true })

    extend('password', (value: string) => {

      if (!passwordExpressionRegex.test(value)) {
        return t('Common.passwordFieldFormatError') as string
      }

      if (draft.value.password
      && draft.value.confirmPassword
      && draft.value.password !== draft.value.confirmPassword) {
        return t('Common.passwordFieldMatchError') as string
      }

      return true

    })

    const handleSubmit = async () => {

      errorMessage.value = null

      await form.value.validate().then(async (valid) => {

        if (!valid) {
          return
        }

        loading.value = true

        try {

          await applyResetPassword({
            token: currentRoute.value.query.token,
            password: draft.value.password,
          })

          router.push({
            name: 'LoginPage',
            query: {
              resetPassword: 'true',
              newSession: 'true',
            },
          })

        } catch (error) {

          if (error.request && !error.response) {
            errorMessage.value = t('ResetPasswordUpdatePage.error.errorNetwork') as string
            return
          }

          // The user's password has already been used. Ask for a different one
          if (error.message === 'PasswordHistory') {
            errorMessage.value = t('ResetPasswordUpdatePage.error.passwordHistory') as string
            return
          }

          // The user's token has expired or is invalid. Ask for a new one
          if (['TokenExpired', 'TokenNotFound'].includes(error.message)) {
            router.push({
              name: 'ResetPasswordPage',
              query: {
                tokenExpired: 'true',
              },
            })
            return
          }

          router.replace({ name: 'UnknownErrorPage' })

        } finally {
          loading.value = false
        }

      })

    }

    onMounted(async () => {

      if (currentRoute.value.query.token && currentRoute.value.query.email) {
      // Cache the user's email address for subsequent login following reset
        user.value.email = currentRoute.value.query.email as string
      } else {
        router.replace({ name: 'UnknownErrorPage' })
      }

      return authService.checkSession(tenant.value)
        .then(() => {

          // User is logged in, they cannot activate their account without logging out
          dialogService({
            title: t('ResetPasswordUpdatePage.error.mustLogoutTitle'),
            bodyContent: t('ResetPasswordUpdatePage.error.mustLogoutMessage'),
            confirmButtonText: t('ResetPasswordUpdatePage.error.mustLogoutConfirm'),
            showCancel: false,
            closeOnClickModal: false,
            closeOnPressEscape: false,
            beforeClose: (close) => {
              router.push({
                name: 'LogoutPage',
                query: {
                  redirectUri: window.location.href,
                },
              })
              return close()
            },
            beforeConfirm: (confirm) => {
              router.push({
                name: 'LogoutPage',
                query: {
                  redirectUri: window.location.href,
                },
              })
              return confirm()
            },
          })

        })
        .catch(noop)

    })

    return {
      t,
      handleSubmit,
      draft,
      form,
      password,
      confirmPassword,
      loading,
      errorMessage,
      user,
    }

  },

})

</script>

<template>
  <default-layout :title="t('ResetPasswordUpdatePage.pageTitle')">
    <sm-form
      ref="form"
      class="reset-form"
      @submit="handleSubmit"
    >
      <template #default>
        <fieldset
          data-heap-redact-attributes="value"
          :disabled="loading"
        >
          <input
            type="email"
            name="email"
            class="sr-only"
            autocomplete="username"
            :value="user.email"
          >
          <sm-input
            data-heap-redact-text="true"
            ref="password"
            v-model="draft.password"
            type="password"
            name="password"
            autocomplete="new-password"
            autofocus
            :label="t('ResetPasswordUpdatePage.passwordLabel')"
            :placeholder="t('ResetPasswordUpdatePage.passwordPlaceholder')"
            rules="required|password"
          />

          <sm-input
            data-heap-redact-text="true"
            ref="confirmPassword"
            v-model="draft.confirmPassword"
            type="password"
            name="confirmPassword"
            autocomplete="new-password"
            :label="t('ResetPasswordUpdatePage.confirmPasswordLabel')"
            :placeholder="t('ResetPasswordUpdatePage.confirmPasswordPlaceholder')"
            rules="required|password"
          />

          <sm-button
            native-type="submit"
            type="primary"
            class="block-button"
            :loading="loading"
          >
            {{ t('ResetPasswordUpdatePage.submitButton') }}
          </sm-button>

          <div class="text-center">
            <router-link
              class="back-to-login-link"
              :to="{ name: 'LoginPage' }"
            >
              {{ t('ResetPasswordUpdatePage.backToLoginLink') }}
            </router-link>
          </div>
        </fieldset>

        <fieldset>
          <sm-help-card
            v-if="errorMessage"
            type="warning"
          >
            <template #header>
              {{ errorMessage }}
            </template>
          </sm-help-card>
        </fieldset>

      </template>
    </sm-form>
  </default-layout>
</template>

<style lang="scss" scoped>

.reset-form .block-button {
  @apply w-full;
  margin-bottom: 22px;
}

::v-deep .reset-form .block-button .sm-button__content {
  @apply block;
}

</style>
